interface Metric {
    value: number;
    labels?: Record<string, string>;
}

interface ParsedMetrics {
    [key: string]: Metric | Metric[] | number;
}

interface DeploymentData {
    headBlock: number;
    delta: number;
    syncSpeed: number;
    eta: number;
}

export interface GraphServerData {
    chainHeadCacheLatestBlock: number;
    deploymentHeads: Record<string, DeploymentData>;
    erpc_upstream_block_head_lag?: number;
    erpc_upstream_finalization_lag?: number;
    erpc_upstream_finalized_block_number?: number;
    erpc_upstream_latest_block_number?: number;
}

export async function fetchPrometheusMetrics(url: string): Promise<GraphServerData> {
    try {
        const response = await fetch(url);
        const metrics = await response.text();
        const parsedMetrics = parsePrometheusMetrics(metrics);
        
        let chainHeadCacheLatestBlock = 0;
        const deploymentHeads: Record<string, DeploymentData> = {};
        const syncSpeeds: Record<string, number> = {};
        const processedCounts: Record<string, number> = {};
        const queryBlocksBehind: Record<string, number> = {};
        
        // Find chain head cache latest block
        chainHeadCacheLatestBlock = (parsedMetrics.chain_head_cache_latest_block as Metric[])[0]!.value;
        
        // Get sync speeds and processed counts for all deployments
        if (Array.isArray(parsedMetrics.deployment_blocks_processed_secs)) {
            parsedMetrics.deployment_blocks_processed_secs.forEach(metric => {
                if (metric.labels?.deployment) {
                    syncSpeeds[metric.labels.deployment] = metric.value;
                }
            });
        }

        if (Array.isArray(parsedMetrics.deployment_blocks_processed_count)) {
            parsedMetrics.deployment_blocks_processed_count.forEach(metric => {
                if (metric.labels?.deployment) {
                    processedCounts[metric.labels.deployment] = metric.value;
                }
            });
        }
        
        // Get query blocks behind metrics - use COUNT not SUM
        if (Array.isArray(parsedMetrics.query_blocks_behind_count)) {
            parsedMetrics.query_blocks_behind_count.forEach(metric => {
                if (metric.labels?.deployment) {
                    queryBlocksBehind[metric.labels.deployment] = metric.value;
                }
            });
        }
        
        // Find all deployment heads - prioritize deployment_head metrics
        if (Array.isArray(parsedMetrics.deployment_head)) {
            parsedMetrics.deployment_head.forEach(metric => {
                if (metric.labels?.deployment) {
                    const deploymentId = metric.labels.deployment;
                    const headBlock = metric.value;
                    const delta = chainHeadCacheLatestBlock - headBlock;
                    const blocksProcessed = processedCounts[deploymentId] || 0;
                    const timeSpent = syncSpeeds[deploymentId] || 0;
                    const avgTimePerBlock = blocksProcessed > 0 ? timeSpent / blocksProcessed : 0;
                    const eta = avgTimePerBlock > 0 ? delta * avgTimePerBlock : 0;
                    
                    deploymentHeads[deploymentId] = {
                        headBlock,
                        delta,
                        syncSpeed: avgTimePerBlock,
                        eta
                    };
                }
            });
        }
        
        // If no deployment_head metrics, create entries from query_blocks_behind
        // This is a fallback for servers that only have query metrics
        Object.keys(queryBlocksBehind).forEach(deploymentId => {
            if (!deploymentHeads[deploymentId]) {
                // Create a placeholder entry for deployments without deployment_head
                // Use chain head as estimated head block minus the behind value
                const estimatedHeadBlock = Math.max(0, chainHeadCacheLatestBlock - queryBlocksBehind[deploymentId]);
                deploymentHeads[deploymentId] = {
                    headBlock: estimatedHeadBlock,
                    delta: queryBlocksBehind[deploymentId],
                    syncSpeed: 0,
                    eta: 0
                };
            }
        });
        
        // Parse RPC proxy metrics
        let erpc_upstream_block_head_lag = 0;
        let erpc_upstream_finalization_lag = 0;
        let erpc_upstream_finalized_block_number = 0;
        let erpc_upstream_latest_block_number = 0;
        
        if (Array.isArray(parsedMetrics.erpc_upstream_block_head_lag)) {
            erpc_upstream_block_head_lag = (parsedMetrics.erpc_upstream_block_head_lag as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_finalization_lag)) {
            erpc_upstream_finalization_lag = (parsedMetrics.erpc_upstream_finalization_lag as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_finalized_block_number)) {
            erpc_upstream_finalized_block_number = (parsedMetrics.erpc_upstream_finalized_block_number as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_latest_block_number)) {
            erpc_upstream_latest_block_number = (parsedMetrics.erpc_upstream_latest_block_number as Metric[])[0]!.value;
        }
        
        return {
            chainHeadCacheLatestBlock,
            deploymentHeads,
            erpc_upstream_block_head_lag,
            erpc_upstream_finalization_lag,
            erpc_upstream_finalized_block_number,
            erpc_upstream_latest_block_number
        };
    } catch (error) {
        console.error('Error fetching Prometheus metrics:', error);
        throw error;
    }
}

function parsePrometheusMetrics(metricsText: string): ParsedMetrics {
    const lines = metricsText.split('\n');
    const parsedMetrics: ParsedMetrics = {};
    
    lines.forEach(line => {
        // Skip comments and empty lines
        if (line.startsWith('#') || line.trim() === '') {
            return;
        }
        
        // Parse metric line
        const [metricName, value] = line.split(' ');
        if (metricName && value) {
            // Handle metrics with labels
            if (metricName.includes('{')) {
                const [name, labels] = metricName.split('{');
                const cleanLabels = labels.replace('}', '');
                const labelPairs = cleanLabels.split(',');
                const labelObject: Record<string, string> = {};
                
                labelPairs.forEach(pair => {
                    const [key, value] = pair.split('=');
                    if (key && value) {
                        labelObject[key] = value.replace(/"/g, '');
                    }
                });
                
                if (!parsedMetrics[name]) {
                    parsedMetrics[name] = [];
                }
                (parsedMetrics[name] as Metric[]).push({
                    value: parseFloat(value),
                    labels: labelObject
                });
            } else {
                parsedMetrics[metricName] = parseFloat(value);
            }
        }
    });
    
    return parsedMetrics;
} 
